import { useEffect, useMemo, useState } from 'react'
import { subscribeToCollection, upsertDocument, deleteDocument } from '@/lib/firestore-crud'

type CrudRecord = {
  id?: string
  updatedAt?: any
  createdAt?: any
}

export function useFirestoreCrud<T extends CrudRecord>(
  collectionPath: string
) {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = subscribeToCollection<T>(collectionPath, (data) => {
      // Sort by updatedAt descending by default for better UI
      const sorted = [...data].sort((a, b) => {
        const timeA = a.updatedAt?.seconds || 0
        const timeB = b.updatedAt?.seconds || 0
        return timeB - timeA
      })
      setItems(sorted)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [collectionPath])

  const upsert = async (item: Record<string, any>) => {
    const id = item.id || crypto.randomUUID()
    const { id: _id, ...data } = item
    await upsertDocument(collectionPath, id, data)
  }

  const remove = async (id: string) => {
    await deleteDocument(collectionPath, id)
  }

  const createItem = async (item: Record<string, any>) => {
    await upsert(item)
  }

  const updateItem = async (id: string, item: Record<string, any>) => {
    await upsert({ id, ...item })
  }

  const deleteItem = async (id: string) => {
    await remove(id)
  }

  const reset = () => {
    // Reset operation is dangerous in production Firestore, so we skip it
    console.warn('Reset operation is disabled for Firestore CRUD implementation.')
  }

  return useMemo(
    () => ({
      items,
      loading,
      upsert,
      remove,
      createItem,
      updateItem,
      deleteItem,
      reset,
    }),
    [items, loading, collectionPath]
  )
}
